import { hasMinMax, type BuilderField, type CustomFieldType } from './customFields';

/** A field's settings (required, default, options, min / max): template fields and fields added on a contact. */

export type SettingChange = <K extends keyof BuilderField>(key: K, value: BuilderField[K]) => void;

const inputCls = 'input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50';
const checkCls = 'h-4 w-4 rounded border-border accent-primary';

const boundInputType = (type: CustomFieldType) => (type === 'Date' ? 'date' : 'number');

export function RequiredCheckbox({
  field,
  onChange,
  disabled,
}: {
  field: BuilderField;
  onChange: SettingChange;
  disabled?: boolean;
}) {
  return (
    <label className="flex h-9 cursor-pointer items-center gap-2 text-xs font-semibold text-foreground">
      <input
        type="checkbox"
        className={checkCls}
        checked={field.is_required}
        disabled={disabled}
        onChange={(e) => onChange('is_required', e.target.checked)}
      />
      Required
    </label>
  );
}

/** Default, Options (Multi Select) and Min / Max (number and date types). */
export function FieldSettingsRow({
  field,
  onChange,
  disabled,
}: {
  field: BuilderField;
  onChange: SettingChange;
  disabled?: boolean;
}) {
  const type = field.field_type;
  return (
    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-foreground">Default</label>
          {type !== 'Boolean' && (
            <input
              type="checkbox"
              className={checkCls}
              checked={field.enable_default}
              disabled={disabled}
              onChange={(e) => onChange('enable_default', e.target.checked)}
            />
          )}
        </div>
        {type === 'Boolean' ? (
          <label className="flex h-9 cursor-pointer items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              className={checkCls}
              checked={field.default_checked}
              disabled={disabled}
              onChange={(e) => onChange('default_checked', e.target.checked)}
            />
            Default Checked
          </label>
        ) : type === 'Textarea' ? (
          <textarea
            disabled={disabled || !field.enable_default}
            rows={3}
            value={field.field_default}
            onChange={(e) => onChange('field_default', e.target.value)}
            placeholder="Default text"
            className="input-embossed w-full rounded-md px-2.5 py-2 text-sm disabled:opacity-50"
          />
        ) : (
          <input
            disabled={disabled || !field.enable_default}
            type={type === 'Date' ? 'date' : type === 'Integer' || type === 'Float' ? 'number' : 'text'}
            step={type === 'Integer' ? 1 : type === 'Float' ? 'any' : undefined}
            value={field.field_default}
            onChange={(e) => onChange('field_default', e.target.value)}
            placeholder={type === 'Multi Select' ? 'Default option(s), comma separated' : 'Text'}
            className={inputCls}
          />
        )}
      </div>

      {type === 'Multi Select' && (
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-foreground">
            Options <span className="text-destructive">*</span>
          </label>
          <input
            value={field.options}
            disabled={disabled}
            onChange={(e) => onChange('options', e.target.value)}
            placeholder="Option1, Option2, Option3"
            className={inputCls}
          />
          <p className="text-[11px] text-muted-foreground">Separate options with commas</p>
        </div>
      )}

      {hasMinMax(type) &&
        (['min', 'max'] as const).map((which) => {
          const enableKey = which === 'min' ? 'enable_min' : 'enable_max';
          const valueKey = which === 'min' ? 'field_min' : 'field_max';
          return (
            <div key={which} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold capitalize text-foreground">{which}</label>
                <input
                  type="checkbox"
                  className={checkCls}
                  checked={field[enableKey]}
                  disabled={disabled}
                  onChange={(e) => onChange(enableKey, e.target.checked)}
                />
              </div>
              <input
                disabled={disabled || !field[enableKey]}
                type={boundInputType(type)}
                step={type === 'Integer' ? 1 : 'any'}
                value={field[valueKey]}
                onChange={(e) => onChange(valueKey, e.target.value)}
                placeholder={type === 'Date' ? '' : which === 'min' ? 'Minimum' : 'Maximum'}
                className={inputCls}
              />
            </div>
          );
        })}
    </div>
  );
}
